import { Helmet } from 'react-helmet-async';
import usePrerenderReady from '../hooks/usePrerenderReady';
import { SITE_URL } from '../seoConfig';
import { buildArticleSchema, buildBreadcrumbSchema, toJsonLd } from '../schema';

const TITLE = 'How Consensus Fantasy Football Rankings Are Calculated — RosterRx';
const DESCRIPTION =
  'How RosterRx builds consensus fantasy football rankings from ESPN, Yahoo, and FantasyPros: what averaging ranks means, why sources disagree, and how to read the standard deviation column.';
// Update by hand only when this page's explanation of the methodology actually changes.
const LAST_UPDATED = '2026-08-31';
const ARTICLE_SCHEMA = buildArticleSchema({
  path: '/how-rankings-are-calculated',
  title: TITLE,
  description: DESCRIPTION,
  datePublished: LAST_UPDATED,
  dateModified: LAST_UPDATED,
});
const BREADCRUMB_SCHEMA = buildBreadcrumbSchema({
  path: '/how-rankings-are-calculated',
  title: 'How Rankings Are Calculated',
});

/**
 * Target query/intent: "how are fantasy football consensus rankings calculated" / "what does
 * average rank mean". Explains RosterRx's own aggregation methodology -- product mechanics a
 * competitor's static article can't speak to directly (R5.6, first of the Phase 4 evergreen set).
 */
function ConsensusRankingsExplainedView() {
  usePrerenderReady();

  return (
    <div>
      <Helmet>
        <title>{TITLE}</title>
        <meta name='description' content={DESCRIPTION} />
        <link rel='canonical' href={`${SITE_URL}/how-rankings-are-calculated`} />
        <meta property='og:title' content={TITLE} />
        <meta property='og:description' content={DESCRIPTION} />
        <meta property='og:url' content={`${SITE_URL}/how-rankings-are-calculated`} />
        <meta property='og:type' content='article' />
        <meta name='twitter:card' content='summary' />
        <meta name='twitter:title' content={TITLE} />
        <meta name='twitter:description' content={DESCRIPTION} />
        <script type='application/ld+json'>{toJsonLd(ARTICLE_SCHEMA)}</script>
        <script type='application/ld+json'>{toJsonLd(BREADCRUMB_SCHEMA)}</script>
      </Helmet>
      <div className='card'>
        <h2 style={{ marginTop: 0 }}>How Consensus Rankings Are Calculated</h2>
        <p>
          RosterRx's consensus rankings average each player's rank across every source it has for
          them — currently ESPN, Yahoo, and FantasyPros, plus any sources you've uploaded yourself.
          A player ranked 3rd by one source and 7th by another shows up around 5th in the consensus
          view, with both individual ranks still visible per source so you can see exactly where
          the disagreement comes from.
        </p>
      </div>

      <div className='card'>
        <h2 style={{ marginTop: 0 }}>What the numbers mean</h2>
        <ul style={{ margin: 0, paddingLeft: 20 }}>
          <li style={{ marginBottom: 8 }}>
            <strong>#Ovr / #Pos.</strong> The player's average overall rank and average
            position rank across all sources that ranked them.
          </li>
          <li style={{ marginBottom: 8 }}>
            <strong>Best / Worst.</strong> The highest and lowest individual rank any single
            source gave that player — a quick way to spot a player one source loves and another
            doesn't.
          </li>
          <li>
            <strong>StdDev.</strong> Standard deviation across sources' ranks for that player. A
            low number means the sources largely agree; a high number means there's real
            disagreement worth digging into before you draft or start them.
          </li>
        </ul>
      </div>

      <div className='card'>
        <h2 style={{ marginTop: 0 }}>Why sources disagree</h2>
        <p>
          Different sources weigh things like target share, offensive scheme changes, injury
          risk, and rookie/backup uncertainty differently — that's normal, not a sign one source
          is simply wrong. Consensus rankings smooth out any one source's individual bias without
          hiding the disagreement, which is why every source's individual rank stays visible
          alongside the consensus number rather than being replaced by it.
        </p>
      </div>
    </div>
  );
}

export default ConsensusRankingsExplainedView;
